import { motion, useScroll, useTransform } from "framer-motion"
import {Plane, Sparkles, Heart, Droplets, ArrowRight, ArrowUpRight} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useRef } from 'react'
import HavenNav from '@/components/HavenNav'

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
}

function Home() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  })
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150])
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0])

  const services = [
    {
      icon: Plane,
      title: "Travel Health",
      subtitle: "Haven Travel Health",
      description: "Vaccinations, travel consults, and preventative care for international travel. Be prepared for every journey.",
      feel: "Safe. Prepared. Global.",
      color: "from-[#E8E4DE] to-[#F5F0EB]",
      link: "/travelhealth"
    },
    {
      icon: Sparkles,
      title: "Acne Health",
      subtitle: "Haven Acne Health",
      description: "Medical acne consultations, prescription skincare plans, and barrier repair education for lasting transformation.",
      feel: "Clinical. Confident. Clear.",
      color: "from-[#F9E8E8] to-[#FFF5F3]",
      link: "/acnehealth"
    },
    {
      icon: Heart,
      title: "Menopause Health",
      subtitle: "Haven Menopause Health",
      description: "Hormone support, symptom management, and personalized care plans for this powerful life transition.",
      feel: "Supportive. Empowering. Expert.",
      color: "from-[#EDE4E0] to-[#F5F0ED]",
      link: "/menopausehealth"
    },
    {
      icon: Droplets,
      title: "Wellness Health",
      subtitle: "Haven Wellness Health",
      description: "IV nutrient therapy, vitamin injections, and functional medicine for rejuvenation and longevity.",
      feel: "Rejuvenation. Longevity. Luxury.",
      color: "from-[#E4E8E6] to-[#EEF2F0]",
      link: "/havenwellnesshealth"
    }
  ]

  const testimonials = [
    {
      quote: "The care I received transformed not just my skin, but my confidence. I finally feel seen and supported.",
      author: "Sarah M.",
      service: "Acne Health"
    },
    {
      quote: "Haven made navigating menopause feel empowering rather than isolating. Expert, compassionate, personalized.",
      author: "Jennifer L.",
      service: "Menopause Health"
    },
    {
      quote: "My travel health consult gave me peace of mind. Thorough, professional, and genuinely caring.",
      author: "Michael T.",
      service: "Travel Health"
    }
  ]

  return (
    <div className="min-h-screen bg-[#FDFCFA]">
      {/* Shared Haven Navigation */}
      <HavenNav />

      {/* Hero Section - Full Bleed Twig Style */}
      <section ref={heroRef} className="relative min-h-[88vh] lg:min-h-[100vh] flex items-center overflow-hidden pt-[76px]">
        {/* Background Image with Parallax */}
        <motion.div 
          style={{ y: heroY }}
          className="absolute inset-0"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFCFA]/95 via-[#FDFCFA]/70 to-transparent z-10" />
          <img 
            src="https://static.lumi.new/1b/1b683d6340f9654e2f487c08cc671cbd.jpg"
            alt="Serene wellness"
            className="w-full h-full object-cover"
          />
        </motion.div>

        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-20 max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-16 w-full pt-10 sm:pt-20 lg:pt-32"
        >
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <img
                src="https://static.lumi.new/7b/7b6911708843c4b80790405cfd058237.webp"
                alt="Haven Integrative Health Consulting logo"
                className="block mx-auto lg:mx-0 w-[100px] sm:w-[130px] lg:w-[160px] h-auto mb-5 sm:mb-6 drop-shadow-sm"
              />
              <p className="text-[11px] sm:text-[13px] uppercase tracking-[0.28em] sm:tracking-[0.3em] text-[#9B8E7E] text-center lg:text-left">
                Haven Integrative Health Consulting
              </p>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-[clamp(2rem,7vw,5.5rem)] leading-[1.05] font-light text-[#2C2925] mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Personalized care for every stage of your health journey
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-[16px] sm:text-[18px] text-[#6B6761] mb-10 sm:mb-12 leading-relaxed max-w-lg font-light"
            >
              Clinical expertise. Elevated wellness. A sanctuary where science meets compassion.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a href="https://havenintegrativehealth.janeapp.com/login" target="_blank" rel="noopener noreferrer" className="group px-8 py-4 bg-[#2C2925] text-white rounded-full text-[14px] tracking-[0.02em] hover:bg-[#1a1815] transition-all inline-flex items-center justify-center gap-3">
                Book Consultation
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#services" className="px-8 py-4 text-[#2C2925] rounded-full text-[14px] tracking-[0.02em] border border-[#2C2925]/20 hover:border-[#2C2925]/50 transition-all inline-flex items-center justify-center">
                Explore Services
              </a>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            className="w-[1px] h-12 bg-gradient-to-b from-transparent via-[#9B8E7E] to-transparent"
          />
        </motion.div>
      </section>


      {/* Intro Statement */}
      <section id="about" className="py-20 sm:py-28 lg:py-40">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-16">
          <motion.div
            {...fadeUp}
            transition={{ duration: 1 }}
            className="max-w-4xl"
          >
            <p className="text-[13px] uppercase tracking-[0.3em] text-[#9B8E7E] mb-10">Our Philosophy</p>
            <h2 className="text-[clamp(1.8rem,3.5vw,3.2rem)] leading-[1.3] text-[#2C2925] font-light mb-10" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Haven Integrative Health is a founder-led practice built on clinical expertise and a commitment to preventative, personalized care. We believe healthcare should be accessible, empowering, and meet you wherever you are in your wellness journey.
            </h2>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full overflow-hidden">
                <img 
                  src="https://static.lumi.new/76/76dfba6e83c663cc75ec4145e4879a20.webp" 
                  alt="Dr. Saba Baig"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <p className="text-[14px] text-[#2C2925] font-medium">Dr. Saba Baig</p>
                <p className="text-[13px] text-[#9B8E7E]">PharmD, BSc, CDE, Founder</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Four Pillars Banner */}
      <section className="bg-[#F7F5F2] pt-14 sm:pt-20 lg:pt-32">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-5 lg:px-10">
          <motion.img
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1 }}
            src="https://static.lumi.new/58/58cde9914b11f7da21bfa82cb6f6e6ab.webp"
            alt="Haven Integrative Health Consulting — Four Pillars of Care"
            className="w-full h-auto rounded-[4px] shadow-[0_30px_80px_-30px_rgba(44,41,37,0.25)]"
          />
        </div>
      </section>

      {/* Services - Horizontal Scroll Cards (Twig Style) */}
      <section id="services" className="py-16 sm:py-20 lg:py-32 bg-[#F7F5F2]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-16">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.8 }}
            className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16"
          >
            <div>
              <p className="text-[13px] uppercase tracking-[0.3em] text-[#9B8E7E] mb-4">What We Offer</p>
              <h2 className="text-[clamp(2rem,4vw,3.5rem)] font-light text-[#2C2925]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Four Pillars of Care
              </h2>
            </div>
            <p className="text-[15px] text-[#6B6761] mt-4 lg:mt-0 max-w-md font-light">
              Distinct services, unified by clinical expertise and a commitment to your well-being.
            </p>
          </motion.div>

          {/* Service Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 sm:gap-6">
            {services.map((service, index) => {
              const isExternal = (service as any).external
              const CardTag = isExternal ? "a" : service.link ? Link : "div"
              const cardProps = isExternal
                ? { href: service.link, target: "_blank", rel: "noopener noreferrer" }
                : service.link ? { to: service.link } : {}
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <CardTag
                    {...cardProps as any}
                    className={`group block h-full bg-gradient-to-b ${service.color} rounded-[24px] p-7 sm:p-8 lg:p-10 relative overflow-hidden transition-all hover:shadow-xl hover:-translate-y-1 cursor-pointer`}
                  >
                    {/* Icon */}
                    <div className="w-14 h-14 bg-white/80 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-8 shadow-sm">
                      <service.icon className="w-6 h-6 text-[#9B8E7E]" />
                    </div>

                    {/* Content */}
                    <p className="text-[11px] uppercase tracking-[0.25em] text-[#9B8E7E] mb-3">{service.subtitle}</p>
                    <h3 className="text-[26px] font-light text-[#2C2925] mb-4 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {service.title}
                    </h3>
                    <p className="text-[14px] text-[#6B6761] leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <p className="text-[12px] text-[#9B8E7E] italic tracking-wide">
                      {service.feel}
                    </p>

                    {/* Arrow */}
                    <div className="mt-8 flex items-center gap-2 text-[#2C2925] opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-[13px] tracking-[0.02em]">Learn more</span>
                      <ArrowUpRight className="w-4 h-4" />
                    </div>
                  </CardTag>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Experience / Clinic Section */}
      <section className="py-16 sm:py-20 lg:py-40">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-[28px] overflow-hidden">
                <img 
                  src="https://static.lumi.new/49/49f490d4d93a31618ed5b11ef55f0e97.png"
                  alt="Woman taking a virtual consultation from the comfort of home"
                  className="w-full h-[280px] sm:h-[380px] md:h-[450px] lg:h-[600px] object-cover object-center"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9 }}
              viewport={{ once: true }}
            >
              <p className="text-[13px] uppercase tracking-[0.3em] text-[#9B8E7E] mb-6">The Experience</p>
              <h2 className="text-[clamp(2rem,3.5vw,3rem)] font-light text-[#2C2925] mb-8 leading-[1.2]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                What to expect at Haven
              </h2>
              <div className="space-y-8 mb-10">
                {[
                  { step: "01", text: "Initial consultation with our licensed clinician to understand your unique health story and goals" },
                  { step: "02", text: "No in-person visit required — all consultations are conducted virtually for your convenience" },
                  { step: "03", text: "Personalized treatment plan tailored to your needs with ongoing clinical support" },
                  { step: "04", text: "Free prescription delivery to your door, depending on location" },
                ].map((item) => (
                  <div key={item.step} className="flex gap-6 items-start">
                    <span className="text-[32px] font-light text-[#E0DBD5] leading-none" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {item.step}
                    </span>
                    <p className="text-[16px] text-[#4A4640] leading-relaxed pt-1">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-[15px] text-[#6B6761] leading-relaxed border-l-2 border-[#E0DBD5] pl-6">
                No clinic visits needed. Book your virtual consultation through Jane App and receive care from the comfort of home.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials - Editorial Style */}
      <section className="py-16 sm:py-20 lg:py-32 bg-[#2C2925]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-16">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="text-[13px] uppercase tracking-[0.3em] text-[#9B8E7E] mb-4">Patient Stories</p>
            <h2 className="text-[clamp(2rem,4vw,3.5rem)] font-light text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Stories of transformation
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="border-t border-white/10 pt-8"
              >
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#9B8E7E] mb-6">{testimonial.service}</p>
                <p className="text-[20px] text-white/90 leading-relaxed mb-8 font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  "{testimonial.quote}"
                </p>
                <p className="text-[13px] text-white/50">— {testimonial.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Team Teaser */}
      <section className="py-16 sm:py-20 lg:py-32">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-16">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 sm:gap-12 lg:gap-20 items-center">
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[13px] uppercase tracking-[0.3em] text-[#9B8E7E] mb-6">Your Care Team</p>
              <h2 className="text-[clamp(2rem,3.5vw,3rem)] font-light text-[#2C2925] mb-6 leading-[1.2]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Led by expertise, driven by compassion
              </h2>
              <p className="text-[16px] text-[#6B6761] leading-relaxed mb-8">
                Our team of dedicated healthcare professionals brings together advanced clinical training with a deeply personal approach to care. Every treatment is guided by evidence, delivered with empathy.
              </p>
              <Link 
                to="/team" 
                className="group inline-flex items-center gap-3 text-[14px] text-[#2C2925] border-b border-[#2C2925]/30 pb-1 hover:border-[#2C2925] transition-colors"
              >
                Meet the full team
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-[28px] overflow-hidden">
                <img 
                  src="https://static.lumi.new/76/76dfba6e83c663cc75ec4145e4879a20.webp"
                  alt="Dr. Saba Baig - Founder"
                  className="w-full h-[360px] sm:h-[420px] lg:h-[500px] object-contain object-center bg-[#F5F2EE]"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm p-5 rounded-2xl shadow-lg"
              >
                <p className="text-[15px] font-medium text-[#2C2925]">Dr. Saba Baig</p>
                <p className="text-[12px] text-[#9B8E7E] mt-1">PharmD, BSc, CDE, Founder & Clinical Director</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 lg:py-40 bg-[#F7F5F2] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#E8E4DE] rounded-full blur-[120px] opacity-50" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#E6EBE4] rounded-full blur-[100px] opacity-40" />
        
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-16 relative">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-[clamp(2.2rem,5vw,4rem)] font-light text-[#2C2925] mb-8 leading-[1.1]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Begin your journey to elevated wellness
            </h2>
            <p className="text-[17px] text-[#6B6761] mb-12 font-light leading-relaxed">
              Book your consultation today. We are here to listen, guide, and support you at every step. Planning something special? Explore our <Link to="/havenwellnesshealth#event-inquiry" className="underline hover:text-[#2C2925] transition-colors">corporate IV events in Vancouver</Link>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://havenintegrativehealth.janeapp.com/login" target="_blank" rel="noopener noreferrer" className="group px-8 sm:px-10 py-4 sm:py-5 bg-[#2C2925] text-white rounded-full text-[14px] tracking-[0.02em] hover:bg-[#1a1815] transition-all inline-flex items-center justify-center gap-3">
                Schedule Your Consultation
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link 
                to="/havenwellnesshealth"
                className="px-8 sm:px-10 py-4 sm:py-5 text-[#2C2925] rounded-full text-[14px] tracking-[0.02em] border border-[#2C2925]/20 hover:border-[#2C2925]/50 transition-all inline-flex items-center justify-center"
              >
                Explore IV Therapy
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C2925] text-white py-14 sm:py-16 lg:py-20">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-16 mb-14 sm:mb-16">
            <div>
              <h4 className="text-[28px] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Haven</h4>
              <p className="text-[14px] text-white/60 leading-relaxed">
                Clinical expertise.<br />Elevated wellness.
              </p>
            </div>
            <div>
              <h5 className="text-[12px] uppercase tracking-[0.2em] text-white/40 mb-5">Services</h5>
              <ul className="space-y-3 text-[14px] text-white/70">
                <li><Link to="/travelhealth" className="hover:text-white transition-colors">Travel Health</Link></li>
                <li><Link to="/acnehealth" className="hover:text-white transition-colors">Acne Health</Link></li>
                <li><Link to="/menopausehealth" className="hover:text-white transition-colors">Menopause Health</Link></li>
                <li><Link to="/havenwellnesshealth" className="hover:text-white transition-colors">Wellness & IV Therapy</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[12px] uppercase tracking-[0.2em] text-white/40 mb-5">Company</h5>
              <ul className="space-y-3 text-[14px] text-white/70">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/team" className="hover:text-white transition-colors">Our Team</Link></li>
                <li><a href="https://havenintegrativehealth.janeapp.com/login" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Book Consultation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">FAQs</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-[12px] uppercase tracking-[0.2em] text-white/40 mb-5">Legal</h5>
              <ul className="space-y-3 text-[14px] text-white/70">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[13px] text-white/40">
              &copy; 2024 Haven Integrative Health Consulting Inc. All rights reserved.
            </p>
            <p className="text-[13px] text-white/40">
              Vancouver, British Columbia
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Home